import { Component, For, createMemo } from 'solid-js';
import { A, useLocation, useNavigate } from '@solidjs/router';
import { TbLayoutDashboard, TbClick, TbCheckbox, TbChartBar, TbSettings, TbLogout } from 'solid-icons/tb';
import { logout } from '../../stores/authStore';
import { config } from '../../config/environment';
import Logo from '../ui/Logo';
import type { JSX } from 'solid-js';
import { version } from '../../../../package.json';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface MenuItem {
  name: string;
  path: string;
  icon: () => JSX.Element;
}

const menuItems: MenuItem[] = [
  { name: 'Dashboard', path: '/dashboard', icon: () => <TbLayoutDashboard size={20} /> },
  { name: 'Clicks', path: '/clicks', icon: () => <TbClick size={20} /> },
  { name: 'Conversions', path: '/conversions', icon: () => <TbCheckbox size={20} /> },
  { name: 'Report', path: '/report', icon: () => <TbChartBar size={20} /> },
];

/**
 * Sidebar Navigation Component
 * Collapsible sidebar with navigation menu
 */
const Sidebar: Component<SidebarProps> = (props) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Reactive memo to compute activeIndex whenever location changes
  const activeIndex = createMemo(() => {
    const index = menuItems.findIndex((item) => isActive(item.path));
    return index >= 0 ? index : 0;
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile Overlay */}
      <div
        class={`fixed inset-0 bg-gray-900/50 z-20 lg:hidden transition-opacity ${
          props.isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={props.onToggle}
      />

      {/* Sidebar */}
      <aside
        class={`fixed top-0 left-0 z-30 h-screen bg-body dark:bg-dark border-r border-dashed border-gray-200 dark:border-gray-700 transition-all duration-300 font-body ${
          props.isOpen ? 'w-64' : 'w-16'
        } lg:translate-x-0 ${props.isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Logo/Brand */}
        <div class="h-16 flex items-center justify-between px-4 border-b border-dashed border-gray-200 dark:border-gray-700">
          <div class={`flex items-center ${!props.isOpen && 'lg:justify-center lg:w-full'}`}>
            <Logo size={props.isOpen ? 40 : 32} showText={props.isOpen} />
          </div>

        </div>

        {/* Navigation Menu */}
        <nav class="p-4 flex flex-col h-[calc(100vh-8rem)]">
          {/* Menu Items with Moving Background Indicator */}
          <div class="relative" style="height: fit-content;">
            {/* Moving Active Indicator Background */}
            <div
              class="absolute left-0 right-0 h-10 bg-orange-500 rounded transition-transform duration-300 ease-out pointer-events-none will-change-transform"
              style={{
                transform: `translateY(calc(${activeIndex()} * (2.5rem + 0.5rem)))`,
              }}
            />

            {/* Menu Items */}
            <div class="relative z-10 flex flex-col gap-2">
              <For each={menuItems}>
                {(item) => (
                  <A
                    href={item.path}
                    class={`flex items-center gap-3 px-3 py-2.5 rounded transition-colors duration-200 font-body relative h-10 focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 focus:shadow-lg focus-visible:shadow-lg focus:scale-105 focus-visible:scale-105 ${
                      isActive(item.path)
                        ? 'text-white'
                        : 'text-gray-700 dark:text-gray-300 border border-transparent hover:bg-gray-100 dark:hover:bg-gray-900 hover:text-orange-500'
                    } ${!props.isOpen && 'lg:justify-center'}`}
                  >
                    {item.icon()}
                    {props.isOpen && (
                      <span class="font-medium whitespace-nowrap">{item.name}</span>
                    )}
                  </A>
                )}
              </For>
            </div>
          </div>

          {/* Spacer */}
          <div class="flex-1"></div>

          {/* Logout Button - Bottom of Nav Container */}
          <button
            onClick={handleLogout}
            class={`flex items-center gap-3 px-3 py-2.5 rounded transition-all duration-200 font-body text-red-600 dark:text-red-400 border-2 border-dashed border-red-200 dark:border-red-900 hover:bg-red-50 dark:hover:bg-red-950/30 w-full focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 focus:shadow-lg focus-visible:shadow-lg focus:scale-105 focus-visible:scale-105 ${
              !props.isOpen && 'lg:justify-center'
            }`}
            title="Logout"
          >
            <TbLogout size={20} />
            {props.isOpen && <span class="font-medium whitespace-nowrap">Logout</span>}
          </button>
        </nav>

        {/* Footer */}
        <div class="p-4 border-t border-dashed border-gray-200 dark:border-gray-700">
          {/* Version */}
          <div
            class={`text-xs text-gray-500 dark:text-gray-400 ${
              !props.isOpen && 'lg:text-center'
            }`}
          >
            {props.isOpen ? `v${version} MNX` : `v${version.split('.')[0]}`}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
